
import { ViewStateManager } from '../view/ViewStateManager';
import { CacheController } from '../cache/CacheController';
import { LoadingStateManager } from '@/utils/loading';
import { NavigationOptions } from './types';

/**
 * Manages navigation and view loading across the application
 */
export class NavigationManager {
  private static instance: NavigationManager;
  private viewStateManager: ViewStateManager;
  private cacheController: CacheController;
  private loadingStateManager: LoadingStateManager;
  private cacheExpiration: number;

  private constructor(cacheExpiration: number = 5 * 60 * 1000) {
    this.viewStateManager = ViewStateManager.getInstance();
    this.cacheController = CacheController.getInstance();
    this.loadingStateManager = LoadingStateManager.getInstance();
    this.cacheExpiration = cacheExpiration;
  }

  public static getInstance(cacheExpiration?: number): NavigationManager {
    if (!NavigationManager.instance) {
      NavigationManager.instance = new NavigationManager(cacheExpiration);
    }
    return NavigationManager.instance;
  }

  /**
   * Load a view
   */
  public async loadView(path: string, options?: NavigationOptions): Promise<void> {
    const { prefetch = false, state = {} } = options || {};
    
    // Generate unique loading ID for this request
    const loadingId = !prefetch ? 
      this.loadingStateManager.addRequest(`view:${path}`, 'normal') : '';
    
    try {
      // Check if we have a cached version that's still valid
      const cachedViews = this.viewStateManager.getCachedViews();
      const cachedView = cachedViews[path];
      const now = Date.now();
      
      if (cachedView && cachedView.lastUpdated && 
          (now - cachedView.lastUpdated < this.cacheExpiration)) {
        
        // Use cached view but update state if provided
        if (!prefetch) {
          // Merge the new state with any existing state
          const mergedState = {
            ...cachedView.state,
            ...state
          };
          
          // Update view state with new state
          this.viewStateManager.updateViewState(path, mergedState);
          
          // Update browser history state
          window.history.replaceState({ 
            ...window.history.state,
            usr: mergedState 
          }, '', path);
          
          // Restore scroll position
          if (cachedView.scrollPosition) {
            window.scrollTo(0, cachedView.scrollPosition);
          } else {
            window.scrollTo(0, 0);
          }
        }
        
        if (!prefetch) this.loadingStateManager.completeRequest(loadingId);
        return;
      }

      // Use Cache Controller to prefetch
      this.cacheController.prefetchRouteData(path);

      // Create a basic view state
      const newViewState = {
        path,
        scrollPosition: 0,
        lastUpdated: Date.now(),
        state // Include the provided state
      };

      // Update view state
      if (!prefetch) {
        this.viewStateManager.setCurrentView(newViewState);
        
        // Update browser history state
        window.history.replaceState({ 
          ...window.history.state,
          usr: state 
        }, '', path);
        
        window.scrollTo(0, 0);
      } else {
        // For prefetch, just cache the view without setting as current
        const cachedViews = this.viewStateManager.getCachedViews();
        cachedViews[path] = newViewState;
      }
    } catch (err) {
      console.error("Error loading view:", err);
    } finally {
      if (!prefetch) this.loadingStateManager.completeRequest(loadingId);
    }
  }

  /**
   * Preload a view (without setting as current)
   */
  public async preloadView(path: string): Promise<void> {
    // Also trigger route-based cache prefetching
    this.cacheController.prefetchRouteData(path);
    return this.loadView(path, { prefetch: true });
  }

  /**
   * Create a deep link URL with state encoded in query parameters
   */
  public createDeepLink(path: string, state: Record<string, any> = {}): string {
    const url = new URL(window.location.origin + path);
    
    // Add state as query parameters
    Object.entries(state).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
      }
    });
    
    return url.toString();
  }

  /**
   * Parse state from query parameters (for deep linking)
   */
  public parseDeepLinkState(queryString: string): Record<string, any> {
    const params = new URLSearchParams(queryString);
    const state: Record<string, any> = {};
    
    params.forEach((value, key) => {
      try {
        // Try to parse as JSON first
        state[key] = JSON.parse(value);
      } catch (e) {
        // If not valid JSON, use the raw value
        state[key] = value;
      }
    });
    
    return state;
  }
}
